// Tax calculation utility using Stripe Tax API
// Real-time, accurate tax calculations for all jurisdictions

interface TaxRate {
  taxAmount: number;
  taxRate: number;
  stateName: string;
}

// Stripe Tax calculation function
// Cache for tax calculations to avoid repeated API calls
const taxCache = new Map<string, { result: TaxRate; timestamp: number }>();
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes (tax rates change infrequently)
const REQUEST_TIMEOUT = 5000; // 5 seconds timeout (fail faster)

// Generate cache key from address and amount
const getCacheKey = (subtotal: number, shippingAddress: any): string => {
  return `${subtotal}-${shippingAddress.address}-${shippingAddress.city}-${shippingAddress.state}-${shippingAddress.postal_code}`;
};

// Clean expired cache entries
const cleanCache = () => {
  const now = Date.now();
  for (const [key, value] of taxCache.entries()) {
    if (now - value.timestamp > CACHE_DURATION) {
      taxCache.delete(key);
    }
  }
};

// Retry logic with exponential backoff
const retryWithBackoff = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 2,
  baseDelay: number = 1000
): Promise<T> => {
  let lastError: Error;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;
      
      if (attempt === maxRetries) {
        throw lastError;
      }
      
      // Exponential backoff: 1s, 2s, 4s
      const delay = baseDelay * Math.pow(2, attempt);
      console.log(`Tax calculation attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError!;
};

export const calculateTaxWithStripe = async (
  subtotal: number, 
  shippingAddress: {
    address?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  }
): Promise<TaxRate> => {
  console.log('calculateTaxWithStripe called with:', { subtotal, shippingAddress });
  
  if (!shippingAddress || subtotal <= 0) {
    console.log('Tax calculation skipped - no shipping address or invalid subtotal');
    return { taxAmount: 0, taxRate: 0, stateName: '' };
  }

  // Check cache first
  const cacheKey = getCacheKey(subtotal, shippingAddress);
  cleanCache(); // Clean expired entries
  
  const cached = taxCache.get(cacheKey);
  if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
    console.log('Tax calculation: Using cached result');
    return cached.result;
  }
  
  // Additional debug logging
  console.log('Tax calculation proceeding with address details:', {
    address: shippingAddress.address,
    city: shippingAddress.city,
    state: shippingAddress.state,
    postal_code: shippingAddress.postal_code,
    subtotalAmount: subtotal
  });

  try {
    const result = await retryWithBackoff(async () => {
      const { supabase } = await import('@/integrations/supabase/client');
      
      // Create timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Tax calculation timeout')), REQUEST_TIMEOUT);
      });
      
      // Race between the actual request and timeout
      const requestPromise = supabase.functions.invoke('calculate-tax', {
        body: {
          amount: subtotal,
          currency: 'usd',
          shipping: {
            address: shippingAddress
          }
        }
      });

      const { data, error } = await Promise.race([requestPromise, timeoutPromise]);

      if (error) {
        console.error('Stripe Tax API error:', error);
        throw new Error(`Tax API error: ${error.message}`);
      }

      return {
        taxAmount: data.taxAmount || 0,
        taxRate: data.taxRate || 0,
        stateName: data.stateName || ''
      };
    });

    // Cache successful result
    taxCache.set(cacheKey, { result, timestamp: Date.now() });
    
    console.log('Stripe Tax calculation result:', result);
    return result;

  } catch (error) {
    console.error('Tax calculation error:', error);
    
    // Return cached result if available, even if expired
    if (cached) {
      console.log('Tax calculation: Using expired cache due to error');
      return cached.result;
    }
    
    return { taxAmount: 0, taxRate: 0, stateName: 'Error' };
  }
};

export const formatTaxRate = (rate: number): string => {
  return `${(rate * 100).toFixed(2)}%`;
};